import { colorsPalettes, mixins } from "@similarweb/styles";
import { Button } from "@similarweb/ui-components";
import type { IColumnsPickerLiteProps } from "@similarweb/ui-components";
import { ColumnsPickerLite } from "@similarweb/ui-components";
import { ChipDownContainer } from "@similarweb/ui-components";
import { SearchInput } from "@similarweb/ui-components";
import React from "react";
import styled, { css } from "styled-components";
import BoxSubtitle from "pro-features/components/BoxSubtitle/src/BoxSubtitle";
import { PrimaryBoxTitle } from "pro-features/styled components/StyledBoxTitle/src/StyledBoxTitle";
import utils from "app/Shared/utils";
import { DownloadButtonMenu } from "../../../../../components/React/DownloadButtonMenu/DownloadButtonMenu";
import { i18nFilter } from "app/filters/i18nFilter";
import { categoryClassIconFilter, i18nCategoryFilter } from "../../../../../filters/ngFilters";
import { allTrackers } from "../../../../../services/track/track";
import { DownloadExcelContainer, SearchContainer } from "../../../../workspace/StyledComponent";
import { SubTitleReferrals } from "../../../incoming-traffic/StyledComponents";
import { CategoryItem } from "../../../../../components/React/CategoriesDropdown/CategoryDropdown";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { buildFiltersForTable } from "./IncomingTrafficTableUtils";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import DurationService from "app/services/DurationService";
import { isPaidExcelAllowed } from "app/services/PaidExcelService/isPaidExcelAllowed";

interface IIncomingTrafficSingleTableTopProps {
    allCategories: Array<{
        Count: number;
        Name: string;
        Value: number;
    }>;
    topCategories: Array<{
        Count: number;
        Name: string;
        Value: number;
    }>;
    totalSectionData: {
        totalShare: number;
        totalUnGroupedCount: number;
        totalVisits: number;
    };
    onFilterChange: any;
    selectedCategory?: any;
    selectedCategoryId?: string;
    categoryParamName?: string; // need to change category param name (for mmx table only) to fix SIM-36615,
    searchTerm?: string;
    excelLink: string;
    tableColumns: any[];
    onClickToggleColumns: (index) => void;
    referralsTrafficShare: Array<{
        color: string;
        name: string;
        percentage: number;
        value: number;
        valueText: string;
        width: number;
    }>;
    topics: Array<{ Name: string; Value: number }>;
    categoriesData: any[];
    domainMetaData: any[];
    filtersStateObject?: {
        filter: string;
        webSource: string;
    };
    downloadExcelPermitted?: boolean;
    totalCount?: number;
}

const Container = styled.div`
    display: flex;
    align-items: center;

    ${SearchContainer} {
        flex-grow: 1;
    }
`;

const Right = styled.div`
    flex-grow: 0;
    display: flex;
    align-items: center;
    margin-left: 10px;
`;

const CategoryItemContainer = styled(CategoryItem)`
    font-weight: 400;
`;

const Section = styled.div<{ margin?: number }>`
    padding: 0 12px;
    display: flex;
    align-items: center;
    ${({ margin }) =>
        margin &&
        css`
            margin: ${margin}px 0;
        `};
`;

const ChipdownItem = styled.div`
    flex-grow: 0;
    margin-right: 8px;
`;

const Title = styled.div`
    padding: 24px 24px 22px 24px;
    ${mixins.setFont({ $size: 20, $color: colorsPalettes.carbon[500], $weight: 500 })};
`;

const Separator = styled.hr`
    border-top-color: ${colorsPalettes.carbon[50]};
    margin: 0;
`;

const SectionContainer = styled.div`
    padding: 0 24px;
`;

const i18n = i18nFilter();

export class IncomingTrafficSingleTableTop extends React.PureComponent<IIncomingTrafficSingleTableTopProps, any> {
    public static defaultProps = {
        allCategories: [],
        selectedCategory: "",
        topics: [],
        searchTerm: "",
        // need to change category param name to fix SIM-36615,
        // we will use "tableCategory" in url param (instead of "category") for mmx table only
        categoryParamName: "category", // default is "category" as previously for all table except mmx table (for which we set "tableCategory" instead)
    };

    private swNavigator = proNavigator;

    constructor(props) {
        super(props);
        this.state = {
            shouldClearValue: false,
        };
    }

    public render() {
        const { shouldClearValue } = this.state;
        const { webSource, duration, search, tableCategory } = this.swNavigator.getParams();
        const searchTerm = search;
        const durationObject = DurationService.getDurationData(duration);
        const is28d = duration === "28d";
        const [fromDate, toDate] = [durationObject.raw.from, durationObject.raw.to];
        const subTitleFilters = [
            {
                filter: "date",
                value: {
                    from: fromDate.valueOf(),
                    to: toDate.valueOf(),
                    useRangeDisplay: !is28d && fromDate.format("YYYY-MM") !== toDate.format("YYYY-MM"),
                },
            },
            {
                filter: "webSource",
                value: webSource,
            },
        ];
        const excelDownloadUrl = this.props.downloadExcelPermitted ? this.props.excelLink : "";
        const isPaidExcelPermitted = isPaidExcelAllowed();
        return (
            <div>
                <Title>
                    <PrimaryBoxTitle>{i18n("analysis.common.trafficsource.referrals.websites")}</PrimaryBoxTitle>
                    <SubTitleReferrals>
                        <BoxSubtitle filters={subTitleFilters} />
                    </SubTitleReferrals>
                </Title>
                <Separator />
                <SectionContainer>
                    <Section margin={19}>
                        {
                            <ChipdownItem>
                                <ChipDownContainer
                                    width={340}
                                    onClick={this.onSelectCategory}
                                    selectedText={
                                        this.props.selectedCategory
                                            ? this.props.selectedCategory.id.replace(/~/g, " > ").replace(/_/g, " ")
                                            : undefined
                                    }
                                    onCloseItem={this.onClearCategory}
                                    buttonText={i18n("common.category.all")}
                                    searchPlaceHolder={i18n("home.dashboards.wizard.filters.searchCategory")}
                                    hasSearch={true}
                                    noResultsMessage={i18n("dropdown.no.results")}
                                >
                                    {this.getCategoriesOptions()}
                                </ChipDownContainer>
                            </ChipdownItem>
                        }
                        <Button
                            type="flat"
                            isDisabled={this.isClearAllDisabled()}
                            onClick={this.onClearAll}
                        >
                            {i18n("forms.buttons.clearall.text")}
                        </Button>
                    </Section>
                </SectionContainer>
                <Container>
                    <SearchContainer>
                        <SearchInput
                            clearValue={shouldClearValue}
                            defaultValue={searchTerm}
                            debounce={400}
                            onChange={this.onSearch}
                            placeholder={i18n("forms.search.placeholder")}
                        />
                        <Right>
                            <FlexRow>
                                <DownloadExcelContainer>
                                    <DownloadButtonMenu
                                        Excel={true}
                                        downloadUrl={excelDownloadUrl}
                                        exportFunction={this.trackExcelDownload}
                                        excelLocked={!this.props.downloadExcelPermitted}
                                        PaidExcel={isPaidExcelPermitted}
                                        totalCount={this.props.totalCount}
                                    />
                                </DownloadExcelContainer>
                                <div>
                                    <ColumnsPickerLite
                                        {...this.getColumnsPickerLiteProps()}
                                        withTooltip={true}
                                        translations={{
                                            tooltip: i18n("workspace.sales.column.picker"),
                                            getSelectedAmountText: ({ selectedValue, totalValue }) =>
                                                i18n("common.columnsPicker.selectedAmount", {
                                                    selected: selectedValue,
                                                    total: totalValue,
                                                }),
                                            mandatoryField: i18n("common.columnsPicker.mandatoryField"),
                                            apply: i18n("common.apply"),
                                        }}
                                    />
                                </div>
                            </FlexRow>
                        </Right>
                    </SearchContainer>
                </Container>
            </div>
        );
    }

    private getColumnsPickerLiteProps = (): Omit<IColumnsPickerLiteProps, "translations"> => {
        const columns = this.props.tableColumns.reduce((res, col, index) => {
            if (!col.fixed) {
                return [
                    ...res,
                    {
                        key: index.toString(),
                        displayName: col.displayName,
                        visible: col.visible,
                    },
                ];
            }
            return res;
        }, []);
        return {
            columns,
            onColumnToggle: this.onColumnToggle,
            onPickerToggle: () => null,
            maxHeight: 264,
            width: "auto",
        };
    };

    private onColumnToggle = (key) => {
        this.props.onClickToggleColumns(parseInt(key));
    };

    private convertCategory = ({ Count, Name, Sons = [], id }, parentId = null) => {
        const text = `${i18nCategoryFilter()(Name)}${Count ? ` (${Count})` : ``}`;
        return {
            text,
            id,
            isCustomCategory: false,
            isChild: Sons.length === 0,
            icon: categoryClassIconFilter()(id),
            forApi: `${parentId ? `${parentId}~` : ``}${id}`,
        };
    };
    private getCategoriesOptions = () => {
        const categories = utils.manipulateCategories(this.props.allCategories);
        const items = categories.reduce((result, category) => {
            if (category.Sons.length > 0) {
                return [
                    ...result,
                    this.convertCategory(category),
                    ...category.Sons.map((son) => this.convertCategory(son, category.id)),
                ];
            } else {
                return [...result, this.convertCategory(category)];
            }
        }, []);

        return items.map((item, index) => {
            return (
                <CategoryItemContainer
                    {...item}
                    key={index}
                    selected={item.forApi === (this.props.selectedCategory && this.props.selectedCategory.id)}
                />
            );
        });
    };

    private onSearch = (search) => {
        const filters = buildFiltersForTable({
            search: search,
            category: this.props.selectedCategory?.id ?? "",
        });
        this.props.onFilterChange(filters, false);
        this.swNavigator.applyUpdateParams(
            {
                search: search || null,
            },
            { notify: false },
        );
    };

    private onSelectCategory = (category) => {
        const value = category ? category.forApi.replace("~", " > ").replace("_", " ") : "Clear Filter";
        allTrackers.trackEvent("Drop Down", "click", `Table/Category/${value}`);

        const filters = buildFiltersForTable({
            search: this.props.searchTerm,
            category: category?.forApi ?? "",
        });

        this.props.onFilterChange(filters, false);
        const params = {};
        params[this.props.categoryParamName] = category?.forApi || "no-category";
        this.swNavigator.updateQueryParams({ tableCategory: category?.forApi || "no-category" }, { reload: false });
    };

    private onClearCategory = () => {
        this.onSelectCategory(null);
    };

    private isClearAllDisabled = () => {
        // button is disabled unless one of the filters has selected value
        return !(this.props.selectedCategory || this.props.searchTerm);
    };

    private onClearAll = () => {
        const filters = buildFiltersForTable({
            search: "",
            category: "",
        });
        this.props.onFilterChange(filters, false);
        this.setState({
            shouldClearValue: true,
        });
        setTimeout(() => {
            this.setState({ shouldClearValue: false });
        });
        this.swNavigator.updateQueryParams(
            {
                tableCategory: "",
                search: null,
            },
            { reload: false },
        );
        allTrackers.trackEvent("Button", "click", "Table/Clear all");
    };

    private trackExcelDownload = () => {
        allTrackers.trackEvent("Download", "submit-ok", "Table/Excel");
    };
}
